import { useEffect, useRef, useState } from "react";
import { Lock } from "lucide-react";
import { Overline } from "@/components/Bits";
import { REFERENCE_BAND } from "@/data/score";

// Compteur animé, qui respecte la préférence système de mouvement réduit.
const useCountUp = (target, duration = 1100) => {
  const [value, setValue] = useState(0);
  const frame = useRef();

  useEffect(() => {
    if (target === null || target === undefined) return undefined;
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduced) { setValue(target); return undefined; }

    const start = performance.now();
    const tick = (now) => {
      const progress = Math.min(1, (now - start) / duration);
      // Décélération douce, cohérente avec les autres transitions du site.
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(target * eased));
      if (progress < 1) frame.current = requestAnimationFrame(tick);
    };
    frame.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame.current);
  }, [target, duration]);

  return value;
};

// Anneau du score global : arc animé + valeur qui monte.
const ScoreDial = ({ value, label, caption, size = 232 }) => {
  const animated = useCountUp(value ?? 0);
  const radius = size / 2 - 14;
  const circumference = 2 * Math.PI * radius;
  const filled = (circumference * (value ?? 0)) / 100;

  return (
    <div className="relative shrink-0" style={{ width: size, height: size }} data-testid="score-dial">
      <svg width={size} height={size} className="-rotate-90" aria-hidden="true">
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#EFEFF1" strokeWidth="3" />
        {/* Zone de référence, posée sur l'anneau lui-même. */}
        <circle
          cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#D4D4D8" strokeWidth="3"
          strokeDasharray={`${(circumference * (REFERENCE_BAND.to - REFERENCE_BAND.from)) / 100} ${circumference}`}
          strokeDashoffset={`${-(circumference * REFERENCE_BAND.from) / 100}`}
        />
        <circle
          cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#0A0A0A" strokeWidth="7" strokeLinecap="butt"
          strokeDasharray={`${filled} ${circumference}`}
          style={{ transition: "stroke-dasharray 1200ms cubic-bezier(0.16,1,0.3,1)" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
        <span className="font-display text-6xl font-light leading-none tabular-nums">{animated}</span>
        <span className="mt-2 font-mono-dt text-[10px] uppercase tracking-wider text-zinc-400">{caption}</span>
        <span className="mt-3 border-t border-black/10 pt-2 font-mono-dt text-[10px] uppercase tracking-wider">{label}</span>
      </div>
    </div>
  );
};

// Une dimension : valeur, barre animée et zone de référence en filigrane.
const DimensionRow = ({ name, body, value, bandLabel, delay = 0 }) => {
  const animated = useCountUp(value ?? 0, 1000);
  const unavailable = value === null || value === undefined;

  return (
    <div className="bg-white p-6 md:p-7" data-testid={`score-dimension-${name}`}>
      <div className="flex items-baseline justify-between gap-4">
        <p className="font-display text-xl font-light">{name}</p>
        <p className="font-display text-2xl font-light tabular-nums">
          {unavailable ? "—" : animated}
        </p>
      </div>

      <div className="relative mt-4 h-[6px] w-full bg-zinc-100" aria-hidden="true">
        {/* Zone de référence indicative, en gris clair sous la valeur. */}
        <div
          className="absolute inset-y-0 bg-zinc-200"
          style={{ left: `${REFERENCE_BAND.from}%`, width: `${REFERENCE_BAND.to - REFERENCE_BAND.from}%` }}
        />
        {!unavailable && (
          <div
            className="absolute inset-y-0 left-0 bg-black"
            style={{ width: `${value}%`, transition: `width 1100ms cubic-bezier(0.16,1,0.3,1) ${delay}ms` }}
          />
        )}
      </div>

      <div className="mt-3 flex items-baseline justify-between gap-4">
        <p className="text-sm leading-relaxed text-zinc-500">{body}</p>
        <span className="shrink-0 font-mono-dt text-[10px] uppercase tracking-wider text-zinc-400">{bandLabel}</span>
      </div>
    </div>
  );
};

export const ScoreBoard = ({ score, copy }) => (
  <div data-testid="score-board">
    <div className="flex flex-col items-start gap-10 md:flex-row md:items-center md:gap-14">
      <ScoreDial value={score.global} label={copy.bands[score.band]} caption={copy.outOf} />
      <div className="max-w-xl">
        <Overline>{copy.overline}</Overline>
        <h2 className="mt-5 font-display text-3xl font-light leading-tight tracking-tight sm:text-4xl">{copy.title}</h2>
        <p className="mt-5 text-base leading-relaxed text-zinc-600">{copy.lead}</p>
        <p className="mt-5 flex items-center gap-3 font-mono-dt text-[10px] uppercase tracking-wider text-zinc-400">
          <span className="inline-block h-[6px] w-8 bg-zinc-200" aria-hidden="true" />
          {copy.referenceLabel} · {REFERENCE_BAND.from}–{REFERENCE_BAND.to}
        </p>
      </div>
    </div>

    <div className="mt-12 grid gap-px border border-black/10 bg-black/10 md:grid-cols-2">
      {score.dimensions.map((dimension, index) => (
        <DimensionRow
          key={dimension.id}
          name={copy.dimensions[dimension.id].name}
          body={copy.dimensions[dimension.id].body}
          value={dimension.value}
          bandLabel={copy.bands[dimension.band]}
          delay={index * 90}
        />
      ))}

      {/* Sixième dimension annoncée mais volontairement inactive : elle exige
          une deuxième lecture dans le temps, qui n'existe pas encore. */}
      <div className="bg-[#FAFAFA] p-6 md:p-7" data-testid="score-dimension-progression">
        <div className="flex items-baseline justify-between gap-4">
          <p className="font-display text-xl font-light text-zinc-400">{copy.progression}</p>
          <span className="flex items-center gap-2 font-mono-dt text-[10px] uppercase tracking-wider text-zinc-400">
            <Lock className="h-3 w-3" aria-hidden="true" /> {copy.progressionTag}
          </span>
        </div>
        <div className="mt-4 h-[6px] w-full bg-zinc-100" aria-hidden="true" />
        <p className="mt-3 text-sm leading-relaxed text-zinc-400">{copy.progressionBody}</p>
      </div>
    </div>

    <details className="mt-8 border-t border-black/10 pt-5" data-testid="score-method">
      <summary className="cursor-pointer font-mono-dt text-[10px] uppercase tracking-wider text-zinc-500 hover:text-black">
        {copy.methodTitle}
      </summary>
      <p className="mt-4 max-w-3xl text-sm leading-relaxed text-zinc-500">{copy.methodBody}</p>
    </details>
  </div>
);

export default ScoreBoard;
